import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { ReducerUtilityService } from 'src/app/shared/utility-services/reducer-utility.service';
import { UserInfoService } from '../../services/user-info.service';
import { Email } from 'src/app/angular-elements/producer/interfaces/email.interface';
import { ZipCountyStateStructure } from '../../interfaces/zipCountyStateStructure.interface';
import {
  SelectedAgencyStateStructure,
  SELECTED_AGENCY_REDUCER_NAME,
  USER_ROLE,
  LOGIN_INFO_REDUCER_NAME,
  UserInfo,
  UserInfoStateStructure,
  SeniorAgency,
  LoggedInUserInformationService,
  EntitlementsHelperService,
  LINE_OF_BUSINESS_ENUM,
  IndividualMarketAgency,
  BaseAgency,
  setSelectedRegionOrState
} from '@ptl/account-management';
import { CommonUtilityService } from 'src/app/shared/utility-services/common-utility.service';
import { County } from 'src/app/angular-elements/shared/interfaces/zip-county-response.interface';
import { NgRedux } from '@ptl/angular-redux-store';
import { StateStructure } from '@ptl/state';
import { PtlNotificationService, PtlNotificationStyle, ZipCodeAndCountySelectComponent } from '@ptl/global-components';
import { validatorLookupRegistry } from '@ptl/forms';

@Component({
    selector: 'app-email-page',
    templateUrl: './email-page.component.html',
    styleUrls: ['./email-page.component.scss'],
    standalone: false
})
export class EmailPageComponent implements OnInit {

  @ViewChild('form') emailFormElement;
  @ViewChild(ZipCodeAndCountySelectComponent, {static: true}) ptlZipCodeAndCounty : ZipCodeAndCountySelectComponent;

  emailForm: UntypedFormGroup;
  isLoading: boolean = false;

  stateValues: { zipCode: string };
  agenciesForDisplay: BaseAgency[];
  userInfo: UserInfo;
  loginInfo: UserInfoStateStructure;
  isFieldAgent: boolean;
  selectedMarket: LINE_OF_BUSINESS_ENUM;
  saveableCounty: County;
  regionMismatch: boolean;
  saveableZipCode: string;
  zipCodeRegion: string;
  linesOfBusiness = {
    senior: LINE_OF_BUSINESS_ENUM.SENIOR,
    individual: LINE_OF_BUSINESS_ENUM.INDIVIDUAL,
    smallGroup: LINE_OF_BUSINESS_ENUM.SMALL_GROUP
  };
  referralTypes = [{
    name: 'Referral Email',
    value: 'EMAILREFERRAL'
  },
  {
    name: 'Enrollment Kits',
    value: 'ENROLLMENTKIT'
  },
  {
    name: 'Roadmap',
    value: 'ROADMAPKIT'
  }];
  interval;
  timeLeft = 1;
  yearOptions = [];

  @Input() yearArray: string;

  constructor(
    private fb: UntypedFormBuilder,
    private reducerUtilityService: ReducerUtilityService,
    private userInfoService: UserInfoService,
    private loggedInUserService: LoggedInUserInformationService,
    private entitlementsHelperService: EntitlementsHelperService,
    private commonUtilityService: CommonUtilityService,
    private ngRedux: NgRedux<StateStructure>,
    private ptlNotificationService: PtlNotificationService,
  ) {
    this.emailForm = this.fb.group({
      emailId: this.fb.control('', [Validators.required, validatorLookupRegistry.get('email')]),
      npnNumber: this.fb.control('', [Validators.required, Validators.pattern('^[0-9]*$')]),
      agencyNumber: this.fb.control('', [Validators.required, Validators.minLength(4), Validators.pattern('^[a-zA-Z0-9]*$')]),
      agency: this.fb.control('', [Validators.required]),
      referralType: this.fb.control('', [Validators.required]),
      yearField: this.fb.control('', [Validators.required])
    });
  }

  ngOnInit() {
    // agency code
    const zipcountyState: ZipCountyStateStructure = this.reducerUtilityService.getState(
      'ZipCounty'
    );

    if (zipcountyState.zipCode) {
      this.stateValues = {
        zipCode: zipcountyState.zipCode
      };
    }

    const selectedAgencyState: SelectedAgencyStateStructure = this.reducerUtilityService.getReducerFromApplicationState(
      SELECTED_AGENCY_REDUCER_NAME
    );

    /** Default to senior if no agency selected */
    this.selectedMarket = selectedAgencyState.selectedMarket || LINE_OF_BUSINESS_ENUM.SENIOR;

    this.loginInfo = this.reducerUtilityService.getReducerFromApplicationState(
      LOGIN_INFO_REDUCER_NAME
    ) as UserInfoStateStructure;

    this.userInfo = this.loginInfo.userInfo;
    this.agenciesForDisplay = this.entitlementsHelperService.getAgenciesByLineOfBusiness(this.selectedMarket);
    this.isFieldAgent = this.loggedInUserService.isMedicareFieldAgent();


    for (const agency of this.agenciesForDisplay) {

      if (this.selectedMarket === LINE_OF_BUSINESS_ENUM.SENIOR) {

        const seniorAgency = agency as SeniorAgency;

        if (
          seniorAgency.agencyId === this.userInfo.agencyIdPref &&
          seniorAgency.region === this.userInfo.regionPref
        ) {
          this.emailForm.controls['agency'].setValue(seniorAgency);
          this.emailForm.controls['agencyNumber'].setValue(seniorAgency.agencyId);
          if (seniorAgency.npnNo) {
            this.emailForm.controls['npnNumber'].setValue(seniorAgency.npnNo);
          } else {
            this.emailForm.controls['npnNumber'].setValue(this.userInfo.npnNo);
          }
        }
      } else {
        const individualSmallGroupAgency = agency as IndividualMarketAgency;

        if (
          individualSmallGroupAgency.agencyId === this.userInfo.agencyIdPref &&
          individualSmallGroupAgency.region === this.userInfo.regionPref
        ) {
          this.emailForm.controls['agency'].setValue(individualSmallGroupAgency);
          this.emailForm.controls['agencyNumber'].setValue(individualSmallGroupAgency.agencyId);
          if (individualSmallGroupAgency.agencyId) {
            this.emailForm.controls['npnNumber'].setValue(individualSmallGroupAgency.agencyId);
          } else {
            this.emailForm.controls['npnNumber'].setValue(this.userInfo.npnNo);
          }
        }
      }

    }

    if(this.yearArray) {
      this.yearOptions = this.yearArray.split(',');
    } else {
      const currentYear = new Date().getFullYear();
      this.yearOptions = [currentYear.toString(), (currentYear+1).toString()];
    }

  }

  submit(form: UntypedFormGroup) {
    const email: Email = {
      member: false,
      deliveryRecipient: form.value.emailId,
      deliveryMethod: 'Email',
      templateName: 'ProducerSharedMedicareBlank',
      memberProfileId: '',
      groupNumber: '',
      umi: '',
      firstName: '',
      lastName: '',
      dob: '',
      etAttributes: [
        {
          name: 'agentCode',
          value: form.value.npnNumber
        },
        {
          name: 'agencyNumber',
          value: form.value.agencyNumber
        },
        {
          name: 'Company',
          value: form.value.agency.agencyName
        },
        {
          name: 'Brand_Name',
          value: this.saveableCounty && this.saveableCounty.region
        },
        {
          name: 'County',
          value: this.saveableCounty && this.saveableCounty.county
        },
        {
          name: 'user_ID',
          value: this.loginInfo.userId
        },
        {
          name: 'EmailTypePreference',
          value: form.value.referralType
        },
        {
          name: 'year',
          value: form.value.yearField
        }
      ]
    };

    // The resetForm method resets the 'submitted' status of the form, which is necessary to hide mat error messages.
    // By providing the current value of the form, all the values will be preserved initally. After success, some are specifically cleared
    this.emailFormElement.resetForm(this.emailForm.value);

    this.isLoading = true;

    this.userInfoService.sendReferralEmail(email)
    .then((response) => {
      this.resetFields();

      this.ptlNotificationService.open({
        title: 'Success',
        content: 'Email sent!',
        primaryButtonText: 'OK',
        notificationStyle: PtlNotificationStyle.SUCCESS
      });
    })
    .catch((error) => {
      this.ptlNotificationService.open({
        title: 'Error Sending Email',
        content: 'A network error occurred. Please try again later or contact support.',
        primaryButtonText: 'OK',
        notificationStyle: PtlNotificationStyle.DANGER
      });
    })
    .finally(() => {
      this.isLoading = false;
    });
  }

  agencyChange(selectedAgency: SeniorAgency | IndividualMarketAgency) {
    this.emailForm.controls['npnNumber'].setValue(
      (selectedAgency as SeniorAgency).npnNo
    );
    this.emailForm.controls['agencyNumber'].setValue(
      (selectedAgency as SeniorAgency).agencyId
    );
    this.checkRegionMismatch();
  }

  checkRegionMismatch() {
    const selectedAgency: SeniorAgency | IndividualMarketAgency
      = this.emailForm.controls['agency'].value;

    const isTelesalesFlow = this.loggedInUserService.isMedicareTelesales();

    if (
      this.saveableCounty &&
      !isTelesalesFlow &&
      this.commonUtilityService.isAgent()
    ) {
      if (this.selectedMarket === LINE_OF_BUSINESS_ENUM.SENIOR) {
        this.regionMismatch = this.saveableCounty.state !== selectedAgency.region;
      } else {
        this.regionMismatch = this.saveableCounty.region !== selectedAgency.region;
      }
    }

    this.ngRedux.dispatch(setSelectedRegionOrState(selectedAgency.region));
  }

  zipCountyChanged(value: { county: County; zipCode: string }) {
    this.saveableCounty = value.county;
    this.saveableZipCode = value.zipCode;
    this.zipCodeRegion = value.county.region;
    this.checkRegionMismatch();
  }

  resetFields() {
    let fieldsToReset = ['emailId', 'yearField', 'referralType'];

    fieldsToReset.forEach((fieldName) => {
      this.emailForm.controls[fieldName].reset();
    });
  }
}
